import { useEffect, useState } from "react";

import { useQuery, useMutation } from "@tanstack/react-query";
import { Loader2, Sun, Moon, Monitor } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface AppearancePrefs {
  theme: "light" | "dark" | "system";
  compactMode: boolean;
}

function applyTheme(theme: "light" | "dark" | "system") {
  // Bring the <html> class in sync immediately so the UI reflects the
  // selected theme without a full reload.
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  if (theme === "system") {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    root.classList.add(prefersDark ? "dark" : "light");
  } else {
    root.classList.add(theme);
  }
  try { localStorage.setItem("theme", theme); } catch { /* ignore */ }
}

export default function AppearancePage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const [prefs, setPrefs] = useState<AppearancePrefs>({ theme: "system", compactMode: false });

  const { data, isLoading } = useQuery<AppearancePrefs>({
    queryKey: ["/api/preferences/appearance"],
    queryFn: () => api.get<AppearancePrefs>(`/preferences/appearance`, sessionId),
  });

  useEffect(() => { if (data) setPrefs(data); }, [data]);

  const saveMutation = useMutation({
    mutationFn: (next: AppearancePrefs) => api.patch(`/preferences/appearance`, next, sessionId),
    onSuccess: (_d, variables) => {
      applyTheme(variables.theme);
      toast({ title: "Appearance saved" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const onThemeChange = (theme: AppearancePrefs["theme"]) => {
    const next = { ...prefs, theme };
    setPrefs(next);
    applyTheme(theme);
    saveMutation.mutate(next);
  };

  const onCompactToggle = (compactMode: boolean) => {
    const next = { ...prefs, compactMode };
    setPrefs(next);
    saveMutation.mutate(next);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Appearance"
        description="Customize the look and feel of the application"
      />

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="text-lg font-medium">Display Settings</CardTitle>
          <p className="text-sm text-muted-foreground">Adjust how the application looks</p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Theme</div>
              <div className="text-sm text-muted-foreground">Switch between light and dark mode</div>
            </div>
            <div className="flex items-center gap-1 rounded-md border p-1">
              {([
                { value: "light" as const, label: "Light", icon: Sun },
                { value: "dark" as const, label: "Dark", icon: Moon },
                { value: "system" as const, label: "System", icon: Monitor },
              ]).map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  className={`px-3 py-1.5 text-xs rounded-sm inline-flex items-center gap-1.5 ${
                    prefs.theme === value
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted text-muted-foreground"
                  }`}
                  onClick={() => onThemeChange(value)}
                  data-testid={`theme-${value}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Compact Mode</div>
              <div className="text-sm text-muted-foreground">Display more items on screen</div>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={prefs.compactMode}
                onChange={(e) => onCompactToggle(e.target.checked)}
                data-testid="switch-compact-mode"
              />
              <div className="w-9 h-5 bg-muted peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all after:mt-0.5 after:ml-0.5 relative peer-checked:bg-primary" />
            </label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
